import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { PaginatedResponse } from '~/shared/data-contract/pagination';

import type {
    GlobalSearchBySkillIdsParams,
    GlobalSearchJob,
    GlobalSearchParams,
} from '../../../data-contract/global-search';
import { globalSearchJobResponseSchema, globalSearchTermSchema } from '../../../data-contract/global-search';
import { getSortParams } from './common';

export const useGlobalSearchJobsResultQuery = ({
    pagination,
    searchTerm,
    sortedFields,
}: GlobalSearchParams): UseQueryResult<PaginatedResponse<GlobalSearchJob>> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const searchParams: Record<string, number | string> = {
        page: pagination.page,
        searchTerm,
        size: pagination.size,
    };

    if (is.nullOrUndefined(sortedFields) === false && sortedFields?.length > 0) {
        searchParams['sort'] = getSortParams(sortedFields);
    }
    const pathname = '/global/search/jobs';

    return useQuery({
        enabled: globalSearchTermSchema.safeParse(searchTerm).success,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(globalSearchJobResponseSchema, {
                pathname,
                requestOptions: {
                    searchParams,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', searchParams],
    });
};

export const useGlobalSearchJobsBySkillQuery = ({
    pagination,
    skillIds,
    sortedFields,
}: GlobalSearchBySkillIdsParams): UseQueryResult<PaginatedResponse<GlobalSearchJob>> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const searchParams: Record<string, number | string> = {
        page: pagination.page,
        size: pagination.size,
        skillIds: skillIds.join(','),
    };

    if (is.nullOrUndefined(sortedFields) === false && sortedFields?.length > 0) {
        searchParams['sort'] = getSortParams(sortedFields);
    }
    const pathname = '/global/skill-id/jobs';

    return useQuery({
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(globalSearchJobResponseSchema, {
                pathname,
                requestOptions: {
                    searchParams,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', searchParams],
    });
};
